// When, relative to the last injectable dose, symptoms get reported. Pure: no React, no `stored`.

export const TIMING_BUCKETS = [
  { id: '0-24', label: '0–24h', minH: 0, maxH: 24 },
  { id: '24-48', label: '24–48h', minH: 24, maxH: 48 },
  { id: '48-72', label: '48–72h', minH: 48, maxH: 72 },
  { id: '72+', label: '3d+', minH: 72, maxH: Infinity },
];

export const TIMING_MIN_ELIGIBLE = 10; // below this the UI shows "not enough data"
export const MIN_SYMPTOM_REPORTS = 5;  // total reports of a symptom before it can be an insight
export const MIN_PEAK_REPORTS = 3;     // reports inside the peak bucket
export const MIN_PEAK_CHECKINS = 4;    // check-ins inside the peak bucket
export const MIN_LIFT = 2;             // peak rate must be >= 2x the rate everywhere else
export const MIN_RATE_DIFF = 0.25;     // ...and at least this much higher in absolute terms
export const MAX_INSIGHTS = 3;

const HOUR = 3600000;
const isInjectable = (getCmpd, key) => getCmpd(key)?.deliveryType !== 'pill';

// Latest shot time (ms) <= t among `sorted` (ascending by ms), or null.
function lastAtOrBefore(sorted, t) {
  let best = null;
  for (const s of sorted) { if (s.ms <= t) best = s.ms; else break; }
  return best;
}

// The dose a check-in is measured from: the linked compound's last dose when the check-in is
// linked to an injectable that has one, otherwise the last dose of ANY injectable compound.
function anchorFor(checkIn, t, injShots, getCmpd) {
  if (checkIn.linkedCmpd && isInjectable(getCmpd, checkIn.linkedCmpd)) {
    const own = lastAtOrBefore(injShots.filter(s => s.compound === checkIn.linkedCmpd), t);
    if (own !== null) return own;
  }
  return lastAtOrBefore(injShots, t);
}

export function tallyTiming({ checkIns = [], shots = [], getCmpd, sinceMs = 0 }) {
  // Shots are searched across ALL history: the anchor for the first check-in in range may precede the range.
  const injShots = shots
    .filter(s => isInjectable(getCmpd, s.compound))
    .map(s => ({ ms: new Date(s.date).getTime(), compound: s.compound }))
    .filter(s => Number.isFinite(s.ms))
    .sort((a, b) => a.ms - b.ms);

  const nPerBucket = TIMING_BUCKETS.map(() => 0);
  const kBySymptom = {};
  let eligibleCount = 0;

  for (const c of checkIns) {
    const t = new Date(c.date).getTime();
    if (!Number.isFinite(t) || t < sinceMs) continue;
    const anchor = anchorFor(c, t, injShots, getCmpd);
    if (anchor === null) continue;
    const hours = (t - anchor) / HOUR;
    const b = TIMING_BUCKETS.findIndex(x => hours >= x.minH && hours < x.maxH);
    eligibleCount++;
    nPerBucket[b]++;
    // A zero-symptom check-in still counts toward n above: it's a real "no symptoms" observation.
    for (const s of new Set(c.symptoms || [])) {
      (kBySymptom[s] ||= TIMING_BUCKETS.map(() => 0))[b]++;
    }
  }
  return { eligibleCount, nPerBucket, kBySymptom };
}

const EPS = 1e-9; // rates are k/n floats; keep exact-boundary cases (e.g. exactly 2x, exactly 0.25) on the passing side

export function buildSymptomTiming({ checkIns = [], shots = [], getCmpd, sinceMs = 0 }) {
  const { eligibleCount, nPerBucket, kBySymptom } = tallyTiming({ checkIns, shots, getCmpd, sinceMs });
  const insights = [];

  for (const [symptom, ks] of Object.entries(kBySymptom)) {
    const K = ks.reduce((a, b) => a + b, 0);
    if (K < MIN_SYMPTOM_REPORTS) continue;

    // Peak = highest-rate bucket that has enough reports AND enough check-ins; ties go to the earlier bucket.
    let p = -1, pRate = -1;
    ks.forEach((k, i) => {
      const n = nPerBucket[i];
      if (k >= MIN_PEAK_REPORTS && n >= MIN_PEAK_CHECKINS && k / n > pRate) { p = i; pRate = k / n; }
    });
    if (p < 0) continue;

    const kO = K - ks[p];
    const nO = eligibleCount - nPerBucket[p];
    if (nO === 0) continue;
    const rO = kO / nO;
    if (pRate < MIN_LIFT * rO - EPS || pRate - rO < MIN_RATE_DIFF - EPS) continue;

    insights.push({
      symptom,
      bucketId: TIMING_BUCKETS[p].id,
      bucketLabel: TIMING_BUCKETS[p].label,
      peak: { k: ks[p], n: nPerBucket[p], rate: pRate },
      other: { k: kO, n: nO, rate: rO },
      buckets: TIMING_BUCKETS.map((b, i) => ({ id: b.id, label: b.label, k: ks[i], n: nPerBucket[i] })),
    });
  }

  insights.sort((a, b) =>
    (b.peak.rate - b.other.rate) - (a.peak.rate - a.other.rate)
    || b.peak.k - a.peak.k
    || a.symptom.localeCompare(b.symptom));

  return { eligibleCount, insights: insights.slice(0, MAX_INSIGHTS) };
}

export function describeInsight(i) {
  return `${i.peak.k} of ${i.peak.n} check-ins ${i.bucketLabel} after a dose reported this, vs ${i.other.k} of ${i.other.n} at other times.`;
}
